import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import eventBus from '@/plugins/eventBus';
import * as EventsApi from '@/api/events';
import { getEventVideo } from '@/api/events';
import { useDownloadQueue } from '@/stores/downloadQueue';

type MutedRenderStatus = 'rendering' | 'error';

export interface MutedDownloadRequest {
    eventId: number;
    downloadName: string;
    size: number;
    fallbackUrl?: string;
    status: MutedRenderStatus;
    error?: string;
    createdAt: string;
    updatedAt: string;
}

interface StartMutedDownloadPayload {
    eventId: number;
    downloadName: string;
    size: number;
    mutedUrl?: string | null;
    fallbackUrl?: string | null;
}

interface RenderStatusEvent {
    eventId?: number | string;
    engineType?: number | string;
    videoQueStatus?: number | string;
}

const ACTIVE_RENDER_EVENT = 'signalr:NotifyRenderStatus';
const MUTED_RENDER_ENGINE_TYPES = new Set([4, 5]);
const MUTED_RENDER_COMPLETE_STATUSES = new Set([9, 10]);

let renderStatusListenerAttached = false;

function isMutedRenderCompleteEvent(data: RenderStatusEvent): boolean {
    return (
        Number(data.eventId) > 0 &&
        MUTED_RENDER_ENGINE_TYPES.has(Number(data.engineType)) &&
        MUTED_RENDER_COMPLETE_STATUSES.has(Number(data.videoQueStatus))
    );
}

function getErrorMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'response' in error) {
        const response = (error as { response?: { data?: unknown } }).response;
        const data = response?.data;

        if (typeof data === 'string') {
            return data;
        }

        if (typeof data === 'object' && data !== null) {
            const message = (data as { Message?: unknown; message?: unknown }).Message ?? (data as { message?: unknown }).message;

            if (typeof message === 'string') {
                return message;
            }
        }
    }

    if (typeof error === 'string') {
        return error;
    }

    if (error instanceof Error) {
        return error.message;
    }

    return 'Muted render failed';
}

function hasPendingMutedDownload(downloadQueue: ReturnType<typeof useDownloadQueue>, eventId: number): boolean {
    return downloadQueue.downloads.some(
        download =>
            download.eventId === eventId &&
            download.variant === 'muted' &&
            !download.completed &&
            !download.error,
    );
}

export const useMutedDownloadQueue = defineStore('mutedDownloadQueue', () => {
    const downloadQueue = useDownloadQueue();
    const requests = ref<MutedDownloadRequest[]>([]);

    const renderingRequests = computed(() => requests.value.filter(request => request.status === 'rendering'));
    const erroredRequests = computed(() => requests.value.filter(request => request.status === 'error'));

    function attachListener() {
        if (renderStatusListenerAttached) {
            return;
        }

        eventBus.$on(ACTIVE_RENDER_EVENT, handleRenderStatus);
        renderStatusListenerAttached = true;
    }

    function getRequest(eventId: number) {
        return requests.value.find(request => request.eventId === eventId);
    }

    function upsertRequest(payload: StartMutedDownloadPayload, status: MutedRenderStatus, error?: string) {
        const now = new Date().toISOString();
        const existing = getRequest(payload.eventId);

        if (existing) {
            Object.assign(existing, {
                downloadName: payload.downloadName,
                size: payload.size,
                fallbackUrl: payload.fallbackUrl ?? undefined,
                status,
                error,
                updatedAt: now,
            });
            return existing;
        }

        const request: MutedDownloadRequest = {
            eventId: payload.eventId,
            downloadName: payload.downloadName,
            size: payload.size,
            fallbackUrl: payload.fallbackUrl ?? undefined,
            status,
            error,
            createdAt: now,
            updatedAt: now,
        };

        requests.value.push(request);
        return request;
    }

    function removeRequest(eventId: number) {
        const index = requests.value.findIndex(request => request.eventId === eventId);

        if (index >= 0) {
            requests.value.splice(index, 1);
        }
    }

    function clearRequests() {
        requests.value = [];
    }

    function isRendering(eventId: number | null | undefined) {
        if (!eventId) {
            return false;
        }

        return getRequest(eventId)?.status === 'rendering';
    }

    function isQueued(eventId: number | null | undefined) {
        if (!eventId) {
            return false;
        }

        return hasPendingMutedDownload(downloadQueue, eventId);
    }

    async function queueMutedDownload(payload: StartMutedDownloadPayload) {
        if (hasPendingMutedDownload(downloadQueue, payload.eventId)) {
            return;
        }

        const resolvedRequest = getRequest(payload.eventId);
        const fallbackUrl = payload.mutedUrl ?? resolvedRequest?.fallbackUrl ?? payload.fallbackUrl ?? undefined;

        downloadQueue.addDownload({
            eventId: payload.eventId,
            variant: 'muted',
            fallbackUrl,
            name: payload.downloadName,
            size: payload.size,
        });
    }

    async function startMutedDownload(payload: StartMutedDownloadPayload): Promise<'queued' | 'rendering'> {
        attachListener();

        if (payload.mutedUrl) {
            await queueMutedDownload(payload);
            removeRequest(payload.eventId);
            return 'queued';
        }

        upsertRequest(payload, 'rendering');

        try {
            const eventApi = EventsApi;
            await eventApi.createApplyMutedAudioTrackRenderJob(payload.eventId);
            return 'rendering';
        } catch (error) {
            const message = getErrorMessage(error);

            if (message.includes('Existing render in progress')) {
                upsertRequest(payload, 'rendering');
                return 'rendering';
            }

            upsertRequest(payload, 'error', message);
            throw new Error(message);
        }
    }

    async function handleRenderStatus(data: RenderStatusEvent) {
        if (!isMutedRenderCompleteEvent(data)) {
            return;
        }

        const eventId = Number(data.eventId);
        const request = getRequest(eventId);

        if (!request || request.status !== 'rendering') {
            return;
        }

        if (hasPendingMutedDownload(downloadQueue, eventId)) {
            removeRequest(eventId);
            return;
        }

        try {
            const video = await getEventVideo(eventId);

            if (!video?.mutedUrl) {
                upsertRequest(
                    {
                        eventId,
                        downloadName: request.downloadName,
                        size: request.size,
                        fallbackUrl: request.fallbackUrl,
                    },
                    'error',
                    'Muted file is not available yet',
                );
                return;
            }

            await queueMutedDownload({
                eventId,
                downloadName: request.downloadName,
                size: video.totalRawFileSize ?? request.size,
                mutedUrl: video.mutedUrl,
                fallbackUrl: request.fallbackUrl ?? video.mutedUrl,
            });
            removeRequest(eventId);
        } catch (error) {
            upsertRequest(
                {
                    eventId,
                    downloadName: request.downloadName,
                    size: request.size,
                    fallbackUrl: request.fallbackUrl,
                },
                'error',
                getErrorMessage(error),
            );
        }
    }

    return {
        requests: readonly(requests),
        renderingRequests,
        erroredRequests,
        isRendering,
        isQueued,
        startMutedDownload,
        queueMutedDownload,
        removeRequest,
        clearRequests,
    };
});
